import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { Bell, Calendar, Clock, Globe, Info, Mail, User } from "lucide-react";
import { getAppointmentsByIdQueryOptions } from "~/features/dashboard/pages/encounters/api/queries/appointments";
import { FormattedEncounter } from "../../../encounters/types";
import { cn } from "@workspace/web-ui/lib/utils";
import { RecommendedTask } from "../../../encounters/components/recommended-task";
import { PatientDetailsConditional } from "~/features/dashboard/components/patient/patient-details-conditional";
import { NotesContainer } from "~/features/dashboard/components/notes";
import { useState, useEffect, type CSSProperties } from "react";
import { AppointmentStatusCard } from "../../../quick-actions/components/cards/appointment-status";
import { toast } from "sonner";
import { useUpdateAppointmentMutation } from "../../../quick-actions/api/mutations/appointment";
import { quickActionsKeys } from "../../../quick-actions/api/queries";
import { encountersKeys } from "../../../encounters/api/queries";
import { Image } from "@workspace/web-ui/components/image";
import { useSendActionNeededMutation } from "../../../patient-logs/api/mutations";

interface InfoRowProps {
    icon: typeof User | typeof Mail | typeof Calendar | typeof Globe | typeof Bell;
    value: string;
    title?: string;
    testId: string;
}

const InfoRow = ({ icon: Icon, value, title, testId }: InfoRowProps) => {
    if (title) {
        return (
            <div className="grid grid-cols-2 w-[70%] gap-3 items-start" data-testid={testId}>
                <span className="text-sm font-medium text-[##202020CC]">{title}</span>
                <span className="text-sm font-normal text-[##202020CC]">{value}</span>
            </div>
        );
    }

    return (
        <div className="flex gap-3 items-center" data-testid={testId}>
            {Icon && <Icon className="w-4 h-4 text-[#7F8493]" />}
            <span className="text-sm font-normal text-[##202020CC]">{value}</span>
        </div>
    );
};

const formatDate = (dateString?: string, format: "datetime" | "date" = "datetime"): string => {
    if (!dateString) return "N/A";

    try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
            return "N/A";
        }
        if (format === "datetime") {
            return date.toLocaleString();
        }
        return date.toLocaleDateString();
    } catch {
        return "N/A";
    }
};

const isHappeningNow = (dateString?: string): string => {
    if (!dateString) return "Unknown";

    try {
        const appointmentTime = new Date(dateString);
        if (Number.isNaN(appointmentTime.getTime())) {
            return "Unknown";
        }

        const now = new Date();
        const timeDiff = appointmentTime.getTime() - now.getTime();
        const hoursDiff = timeDiff / (1000 * 60 * 60);

        if (Math.abs(hoursDiff) < 1) {
            return "Happening now";
        }

        if (appointmentTime > now) {
            return "Upcoming";
        }

        return "Past";
    } catch {
        return "Unknown";
    }
};

// Skeleton loader for appointment details
function AppointmentDetailsSkeleton() {
    return (
        <div className="flex flex-col w-full h-full bg-white rounded-[24px] border border-[#0404041A] overflow-auto gap-4 sm:gap-5 p-4 sm:py-4 sm:px-4 animate-pulse">
            <div className="h-6 w-1/2 bg-gray-200 rounded mb-2" />
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between">
                <div className="flex flex-col gap-2 w-full sm:w-auto">
                    <div className="h-8 w-2/3 bg-gray-200 rounded mb-1" />
                    <div className="flex gap-2">
                        <div className="h-4 w-20 bg-gray-200 rounded" />
                        <div className="h-4 w-12 bg-gray-200 rounded" />
                        <div className="h-4 w-10 bg-gray-200 rounded" />
                    </div>
                </div>
                <div className="h-10 w-32 bg-gray-200 rounded" />
            </div>
            <div className="flex flex-col gap-2 mt-2 sm:mt-5">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-4 w-full bg-gray-200 rounded" />
                ))}
            </div>
            <div className="h-4 w-1/3 bg-gray-200 rounded mt-2" />
            <div className="h-12 w-full bg-gray-200 rounded mt-2" />
            <div className="h-10 w-1/2 bg-gray-200 rounded mt-2" />
        </div>
    );
}

export const TodoAppointmentDetails = ({ encounter }: { encounter: FormattedEncounter }) => {
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();
    const queryClient = useQueryClient();
    const [localTag, setLocalTag] = useState(encounter.tag);

    useEffect(() => {
        setLocalTag(encounter.tag);
    }, [encounter.tag]);

    const { data: appointment, isLoading, isError } = useQuery(getAppointmentsByIdQueryOptions({
        id: encounter.id ?? "",
    }));

    const UpdateAppointmentMutation = useUpdateAppointmentMutation({
        id: encounter.id ?? ""
    });

    const { mutate: sendActionNeeded, isPending: sendActionpending } = useSendActionNeededMutation();

    const handleMarkAppointmentStatus = async (status: 'attended' | 'cancelled') => {
        const meetingStatus = status === "cancelled" ? "not-held" : "held";
        try {
            await UpdateAppointmentMutation.mutateAsync(
                { status },
                {
                    onSuccess(data) {
                        if (data.status === "success") {
                            setLocalTag(status === "attended" ? "Held" : "Not Held");
                            toast.success(`Appointment marked as ${meetingStatus}`, {
                                style: {
                                    background: "#6EE7B7",
                                    color: "#FFFFFF",
                                    borderColor: "#6EE7B7",
                                    "& svg": {
                                        color: "#FFFFFF",
                                    },
                                } as CSSProperties,
                            });
                        }
                        queryClient.invalidateQueries({ queryKey: quickActionsKeys.appointmentById(encounter.id ?? "") });
                        queryClient.invalidateQueries({ queryKey: encountersKeys.all });
                    },
                    onError() {
                        toast.error(`An error occurred while updating appointment status as ${meetingStatus}`);
                    },
                }
            );
        } catch (error) {
            console.log("error updating appointment status", error);
        }
    };

    if (isLoading) {
        return <AppointmentDetailsSkeleton />;
    }

    if (isError) {
        return null;
    }

    const infoRows = [
        {
            icon: User,
            title: "Name",
            value: appointment?.patient.name,
            testId: 'patient-name'
        },
        {
            icon: User,
            title: "Gender",
            value: appointment?.patient.gender,
            testId: 'patient-gender'
        },
        {
            icon: User,
            title: "Age",
            value: appointment?.patient.age != null ? String(appointment.patient.age) : "",
            testId: 'patient-age'
        },
        {
            icon: Calendar,
            title: "Meeting Time",
            value: `${formatDate(appointment?.meetingStartingTime ?? "", "datetime")}`,
            testId: 'appointment-datetime'
        },
        {
            icon: Globe,
            title: "Location",
            value: appointment?.timezone,
            testId: 'appointment-location'
        },
        {
            icon: Mail,
            title: "Email",
            value: appointment?.patient.email,
            testId: 'patient-email'
        },
        {
            icon: Bell,
            title: "Status",
            value: isHappeningNow(appointment?.meetingStartingTime ?? ""),
            testId: 'appointment-status'
        }
    ];

    const weightLossInfoRows = [
        {
            icon: User,
            value: appointment?.patient.name,
            testId: "patient-name"
        },
        {
            icon: Calendar,
            value: `${formatDate(appointment?.meetingStartingTime ?? "", "datetime")}`,
            testId: "appointment-datetime"
        },
        {
            icon: Globe,
            value: appointment?.timezone,
            testId: "appointment-location"
        },
        {
            icon: Mail,
            value: appointment?.patient.email,
            testId: "patient-email"
        },
        {
            icon: Bell,
            value: isHappeningNow(appointment?.meetingStartingTime ?? ""),
            testId: "appointment-status"
        }
    ];

    return (
        <>
            {program === "weight-loss" && (
                <div className="flex flex-col sm:w-full h-full bg-[#FFFFFF] rounded-[24px] overflow-auto gap-4 sm:gap-5 p-4 sm:py-[16px]">
                    <div className="flex items-center gap-2">
                        <div
                            className={cn(
                                "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit border",
                                localTag?.toLowerCase() === "held"
                                    ? "text-[#16A34A] bg-[#16A34A26] border-[#16A34A1A]"
                                    : localTag?.toLowerCase() === "not held"
                                        ? "text-[#DC2626] bg-[#DC262626] border-[#DC26261A]"
                                        : "text-[#6B7280] bg-[#F3F4F6] border-[#D1D5DB]"
                            )}
                            data-testid="consultation-status"
                        >
                            <Calendar
                                className={cn(
                                    "size-4",
                                    localTag?.toLowerCase() === "held"
                                        ? "text-[#16A34A]"
                                        : localTag?.toLowerCase() === "not held"
                                            ? "text-[#DC2626]"
                                            : "text-[#6B7280]"
                                )}
                            />
                            <span className="text-xs font-medium">
                                {localTag
                                    ? localTag.charAt(0).toUpperCase() + localTag.slice(1)
                                    : "Not Held"}
                            </span>
                        </div>
                        {encounter.recommendedTasks && encounter.recommendedTasks.length > 0 ? (
                            <div
                                className={cn(
                                    "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
                                    "text-[#7B238D] bg-[#7B238D26] border"
                                )}
                                data-testid="consultation-status"
                            >
                                <Clock className="size-4 text-[#7B238D]" />
                                <span className="text-xs font-medium">Resolved</span>
                            </div>
                        ) : (
                            <div
                                className={cn(
                                    "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
                                    "text-[#FFA726] bg-[#FFF5E6]"
                                )}
                                data-testid="consultation-status"
                            >
                                <Clock className="size-4 text-[#FFA726]" />
                                <span className="text-xs font-medium">Action needed</span>
                            </div>
                        )}
                    </div>

                    <h2 className="text-lg sm:text-xl font-medium">{appointment?.description}</h2>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between w-full">
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                            <div className="flex items-center gap-2">
                                <h2 className="text-2xl sm:text-3xl font-bold truncate max-w-full">{appointment?.patient?.name}</h2>
                                {encounter.patient.medical_plan?.payment_status === "paid" &&
                                    encounter.patient.glp1_guide_accepted &&
                                    encounter.patient.identity_verified &&
                                    encounter.patient.supplementary_health_assessment &&
                                    Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                                        <div>
                                            <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                        </div>
                                    )}
                            </div>

                            <div className="flex flex-wrap items-center gap-y-1 gap-x-2">
                                <span className="text-sm" style={{ color: '#2D2F35' }}>
                                    {appointment?.patient.age} years
                                </span>
                                <span className="hidden sm:inline mx-1" style={{ color: '#667085' }}>•</span>
                                <span className="text-sm" style={{ color: '#2D2F35' }}>
                                    {appointment?.patient.gender}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 mt-2 sm:mt-5 w-full">
                        {weightLossInfoRows.map(({ icon, value, testId }) => (
                            <InfoRow key={testId} icon={icon} value={value ?? ""} testId={testId} />
                        ))}
                    </div>

                    <h2 className="text-xs font-normal text-[#202020CC]">Created {appointment?.meetingCreatedAt}</h2>
                    <PatientDetailsConditional encounter={encounter} program={program} />

                    {new Date(`${encounter.date} ${encounter.time}`) > new Date() && (
                        <div className="flex flex-col md:flex-row items-center gap-3 p-4 w-full bg-[#EBF0FB] rounded-lg">
                            <Info className="size-6 text-[#4272DD]" />
                            {encounter.status === "happening" ? (
                                <p className="text-sm font-normal text-[#202020CC]">This Appointment is live.</p>
                            ) : (
                                <p className="text-xs font-normal text-[#202020CC] whitespace-normal">
                                    This appointment is scheduled for {`${encounter.date}`}. Please ensure you're available 5 minutes before the start time.
                                </p>
                            )}

                            {encounter.status === "happening" && (
                                <Link
                                    className={cn(
                                        buttonVariants({ variant: "outline" }),
                                        "bg-transparent w-[135px] text-[#4272DD] justify-self-end rounded-[8px] border-none py-3 px-4 hover:bg-[#4272DD] hover:text-white"
                                    )}
                                    to="/dashboard/$program/patients/patient/$id"
                                    params={{
                                        id: encounter?.patient.id || "",
                                        program
                                    }}
                                    search={{
                                        name: encounter?.patient.name.replace(/\s+/g, "") || "",
                                        sessionId: encounter?.session?.session_id || ""
                                    }}
                                >
                                    View Patient Log
                                </Link>
                            )}

                            <a
                                href={appointment?.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(
                                    buttonVariants({ variant: "default" }),
                                    "bg-[#4272DD] rounded-lg w-full sm:w-auto",
                                    encounter.status !== "happening" ? "pointer-events-none cursor-not-allowed opacity-60 no-underline" : ""
                                )}
                            >
                                Join meeting
                            </a>
                        </div>
                    )}

                    <div className="flex gap-4 w-full flex-wrap">
                        {(encounter.status === "happening" || encounter.status === "past" || encounter.status === "cancelled") && (
                            <div className="flex flex-col w-full">
                                <AppointmentStatusCard
                                    status={encounter.status}
                                    encounter={encounter}
                                    onStatusChange={handleMarkAppointmentStatus}
                                />
                            </div>
                        )}
                    </div>

                    {/* Notes Section */}
                    {["happening", "past", "completed", "attended"].includes(encounter.status) &&
                        encounter.patient?.medical_plan?.payment_status === "paid" &&
                        encounter.patient?.glp1_guide_accepted &&
                        encounter.patient?.identity_verified &&
                        encounter.patient?.supplementary_health_assessment &&
                        Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                            <NotesContainer
                                encounterId={encounter.encounterId}
                                patientId={encounter.patient.id}
                            />
                        )
                    }

                    {["happening", "past", "completed", "attended"].includes(encounter.status) && (
                        <>
                            {!(encounter.patient?.medical_plan?.payment_status === "paid" &&
                                encounter.patient?.glp1_guide_accepted &&
                                encounter.patient?.identity_verified &&
                                encounter.patient?.supplementary_health_assessment &&
                                Object.keys(encounter.patient.supplementary_health_assessment).length > 0) && (
                                <div className="flex items-center bg-[#F0F5FF] rounded-lg px-4 py-2 justify-between w-full">
                                    <p className="text-sm text-[#1A202C]">Patient must be verified</p>
                                    <Button
                                        className="button-primary !bg-[#4272DD] h-[44px] !text-white flex gap-2 items-center !shadow-none w-fit !border-none"
                                        onClick={async () => {
                                            try {
                                                await sendActionNeeded({ patient_id: encounter.patient.id });
                                                toast.success("Reminder sent to patient to complete verification.");
                                            } catch {
                                                toast.error("Failed to send reminder.");
                                            }
                                        }}
                                        disabled={sendActionpending}
                                    >
                                        {sendActionpending && (
                                            <svg
                                                className="animate-spin h-4 w-4 mr-2 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                ></path>
                                            </svg>
                                        )}
                                        Send a Reminder
                                    </Button>
                                </div>
                            )}
                            <RecommendedTask 
                                encounterId={encounter.encounterId} 
                                patientId={encounter.patient.id} 
                                showAddButton={
                                    encounter.patient?.medical_plan?.payment_status === "paid" &&
                                    encounter.patient?.glp1_guide_accepted &&
                                    encounter.patient?.identity_verified &&
                                    encounter.patient?.supplementary_health_assessment &&
                                    Object.keys(encounter.patient.supplementary_health_assessment).length > 0
                                }
                            />
                        </>
                    )}
                </div>
            )}

            {program === "urgent-care" && (
                <div className="flex flex-col sm:w-full h-full bg-[#FFFFFF] rounded-[24px] overflow-auto gap-4 sm:gap-5 p-4 sm:py-[16px]">
                    <div
                        className={cn(
                            "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit border",
                            localTag?.toLowerCase() === "held"
                                ? "text-[#16A34A] bg-[#16A34A26]"
                                : localTag?.toLowerCase() === "not held"
                                    ? "text-[#DC2626] bg-[#DC262626]"
                                    : "text-[#7F8493] bg-[#EDF0F3]"
                        )}
                        data-testid="consultation-status"
                    >
                        <Calendar className={cn(
                            "size-4",
                            localTag?.toLowerCase() === "held"
                                ? "text-[#16A34A]"
                                : localTag?.toLowerCase() === "not held"
                                    ? "text-[#DC2626]"
                                    : "text-[#7F8493]"
                        )} />
                        <span className="text-xs font-medium">{localTag || "Not Held"}</span>
                    </div>

                    {/* Patient info section */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between w-full">
                        <div className="flex flex-col gap-2 w-full">
                            <div className="flex items-center gap-2">
                                <h2 className="text-2xl sm:text-3xl font-bold truncate max-w-full">{appointment?.patient?.name}</h2>
                                {
                                    encounter.patient.urgent_care_onboarded && encounter.patient.identity_verified && (
                                        <div className="">
                                            <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                        </div>
                                    )}
                            </div>

                            <div className="flex flex-col w-full text-left pb-1">
                                <div className="flex justify-between w-[80%]">
                                    <div className="text-[#464B55] text-sm">
                                        Submitted:
                                        <p className="text-[#1A202C] text-sm">{appointment?.meetingCreatedAt}</p>
                                    </div>
                                    <div className="text-[#464B55] text-sm">
                                        Program:
                                        <p className="text-[#1A202C] text-sm">Urgent Care</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Patient information rows */}
                    <div className="flex flex-col gap-3 sm:mt-3 w-full border rounded-md p-3">
                        {infoRows.map(({ icon, value, title, testId }) => (
                            <InfoRow
                                key={testId}
                                icon={icon}
                                value={value ?? ""}
                                title={title}
                                testId={testId}
                            />
                        ))}
                    </div>

                    {new Date(`${encounter.date} ${encounter.time}`) > new Date() && (
                        <div className="flex flex-col md:flex-row items-center gap-3 p-4 w-full bg-[#EBF0FB] rounded-lg">
                            <Info className="size-6 text-[#4272DD]" />
                            {encounter.status === "happening" ? (
                                <p className="text-sm font-normal text-[#202020CC]">This Appointment is live.</p>
                            ) : (
                                <p className="text-xs font-normal text-[#202020CC] whitespace-normal">This appointment is scheduled for {`${encounter.date}`}. Please ensure you're available 5 minutes before the start time. </p>
                            )}

                            {encounter.status === "happening" && (<Link
                                className={cn(buttonVariants({ variant: "outline" }), "bg-transparent w-[135px] text-[#4272DD] justify-self-end rounded-[8px] border-none py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                to="/dashboard/$program/patients/patient/$id"
                                params={{
                                    id: encounter?.patient.id || "",
                                    program
                                }}
                                search={{
                                    name: encounter?.patient.name.replace(/\s+/g, "") || "",
                                    sessionId: encounter?.session?.session_id || ""
                                }}
                            >
                                View Patient Log
                            </Link>)}

                            <a href={appointment?.meetingLink}
                                target="_blank" rel="noopener noreferrer"
                                className={cn(buttonVariants({ variant: "default" }), "bg-[#4272DD] rounded-lg w-full sm:w-auto", encounter.status !== "happening" ? "pointer-events-none cursor-not-allowed opacity-60 no-underline" : "")}>
                                Join meeting
                            </a>
                        </div>
                    )}

                    {["happening", "past", "completed", "attended"].includes(encounter.status) && encounter.patient.urgent_care_onboarded && (
                        <NotesContainer
                            encounterId={encounter.encounterId}
                            patientId={encounter.patient.id}
                            notes={encounter.notes}
                            icd={encounter.session?.icd10_code}
                        />
                    )}
                </div>
            )}
        </>
    );
};

